import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/setup',
    name: 'setup',
    component: () => import('@/views/auth/SetupView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
      // Design Section
      {
        path: 'design',
        name: 'design',
        component: () => import('@/views/design/DesignView.vue'),
        children: [
          {
            path: 'projects/:projectId/stages',
            name: 'design.stages',
            component: () => import('@/views/design/StagesView.vue'),
          },
          {
            path: 'projects/:projectId/stages/new',
            name: 'design.stages.create',
            component: () => import('@/views/design/StageEditView.vue'),
          },
          {
            path: 'projects/:projectId/stages/:stageId',
            name: 'design.stages.edit',
            component: () => import('@/views/design/StageEditView.vue'),
          },
          {
            path: 'projects/:projectId/agents',
            name: 'design.agents',
            component: () => import('@/views/design/AgentsView.vue'),
          },
          {
            path: 'projects/:projectId/agents/new',
            name: 'design.agents.create',
            component: () => import('@/views/design/AgentEditView.vue'),
          },
          {
            path: 'projects/:projectId/agents/:agentId',
            name: 'design.agents.edit',
            component: () => import('@/views/design/AgentEditView.vue'),
          },
          {
            path: 'projects/:projectId/classifiers',
            name: 'design.classifiers',
            component: () => import('@/views/design/ClassifiersView.vue'),
          },
          {
            path: 'projects/:projectId/classifiers/new',
            name: 'design.classifiers.create',
            component: () => import('@/views/design/ClassifierEditView.vue'),
          },
          {
            path: 'projects/:projectId/classifiers/:classifierId',
            name: 'design.classifiers.edit',
            component: () => import('@/views/design/ClassifierEditView.vue'),
          },
          {
            path: 'projects/:projectId/context-transformers',
            name: 'design.contextTransformers',
            component: () => import('@/views/design/ContextTransformersView.vue'),
          },
          {
            path: 'projects/:projectId/context-transformers/new',
            name: 'design.contextTransformers.create',
            component: () => import('@/views/design/ContextTransformerEditView.vue'),
          },
          {
            path: 'projects/:projectId/context-transformers/:transformerId',
            name: 'design.contextTransformers.edit',
            component: () => import('@/views/design/ContextTransformerEditView.vue'),
          },
          {
            path: 'projects/:projectId/tools',
            name: 'design.tools',
            component: () => import('@/views/design/ToolsView.vue'),
          },
          {
            path: 'projects/:projectId/tools/new',
            name: 'design.tools.create',
            component: () => import('@/views/design/ToolEditView.vue'),
          },
          {
            path: 'projects/:projectId/tools/:toolId',
            name: 'design.tools.edit',
            component: () => import('@/views/design/ToolEditView.vue'),
          },
          {
            path: 'projects/:projectId/global-actions',
            name: 'design.globalActions',
            component: () => import('@/views/design/GlobalActionsView.vue'),
          },
          {
            path: 'projects/:projectId/global-actions/new',
            name: 'design.globalActions.create',
            component: () => import('@/views/design/GlobalActionEditView.vue'),
          },
          {
            path: 'projects/:projectId/global-actions/:globalActionId',
            name: 'design.globalActions.edit',
            component: () => import('@/views/design/GlobalActionEditView.vue'),
          },
          {
            path: 'projects/:projectId/guardrails',
            name: 'design.guardrails',
            component: () => import('@/views/design/GuardrailsView.vue'),
          },
          {
            path: 'projects/:projectId/moderation',
            name: 'design.moderation',
            redirect: (to) => ({ name: 'design.guardrails', params: to.params }),
          },
          {
            path: 'projects/:projectId/guardrails/new',
            name: 'design.guardrails.create',
            component: () => import('@/views/design/GuardrailEditView.vue'),
          },
          {
            path: 'projects/:projectId/guardrails/:guardrailId',
            name: 'design.guardrails.edit',
            component: () => import('@/views/design/GuardrailEditView.vue'),
          },
          {
            path: 'projects/:projectId/knowledge',
            name: 'design.knowledge',
            component: () => import('@/views/design/KnowledgeView.vue'),
          },
          {
            path: 'projects/:projectId/global-memory',
            name: 'design.globalMemory',
            component: () => import('@/views/design/GlobalMemoryView.vue'),
          },
          {
            path: 'projects/:projectId/sample-copies',
            name: 'design.sampleCopies',
            component: () => import('@/views/design/SampleCopiesView.vue'),
          },
          {
            path: 'projects/:projectId/documents',
            name: 'design.documents',
            component: () => import('@/views/design/DocumentsView.vue'),
          },
          {
            path: 'projects/:projectId/documents/new',
            name: 'design.documents.create',
            component: () => import('@/views/design/DocumentEditView.vue'),
          },
          {
            path: 'projects/:projectId/documents/:documentId',
            name: 'design.documents.edit',
            component: () => import('@/views/design/DocumentEditView.vue'),
          },
          {
            path: 'projects/:projectId/testing',
            name: 'design.testing',
            redirect: (to) => ({ name: 'testing.scenarios', params: to.params }),
          },
          {
            path: 'projects/:projectId/testers',
            name: 'design.testers',
            redirect: (to) => ({ name: 'testing.testers', params: to.params }),
          },
          {
            path: 'projects/:projectId/testers/new',
            name: 'design.testers.create',
            redirect: (to) => ({ name: 'testing.testers.create', params: to.params }),
          },
          {
            path: 'projects/:projectId/testers/:testerId',
            name: 'design.testers.edit',
            redirect: (to) => ({ name: 'testing.testers.edit', params: to.params }),
          },
          {
            path: 'projects/:projectId/scenarios',
            name: 'design.scenarios',
            redirect: (to) => ({ name: 'testing.scenarios', params: to.params }),
          },
          {
            path: 'projects/:projectId/scenarios/new',
            name: 'design.scenarios.create',
            redirect: (to) => ({ name: 'testing.scenarios.create', params: to.params }),
          },
          {
            path: 'projects/:projectId/scenarios/:scenarioId',
            name: 'design.scenarios.edit',
            redirect: (to) => ({ name: 'testing.scenarios.edit', params: to.params }),
          },
        ],
      },
      // Testing Section
      {
        path: 'testing',
        name: 'testing',
        component: () => import('@/views/testing/TestingView.vue'),
        redirect: { name: 'testing.playground' },
        children: [
          {
            path: 'playground/:projectId?',
            name: 'testing.playground',
            component: () => import('@/views/PlaygroundView.vue'),
          },
          {
            path: 'projects/:projectId/testers',
            name: 'testing.testers',
            component: () => import('@/views/testing/TestersListView.vue'),
          },
          {
            path: 'projects/:projectId/testers/new',
            name: 'testing.testers.create',
            component: () => import('@/views/design/TesterEditView.vue'),
          },
          {
            path: 'projects/:projectId/testers/:testerId',
            name: 'testing.testers.edit',
            component: () => import('@/views/design/TesterEditView.vue'),
          },
          {
            path: 'projects/:projectId/scenarios',
            name: 'testing.scenarios',
            component: () => import('@/views/testing/ScenariosListView.vue'),
          },
          {
            path: 'projects/:projectId/test-runs',
            name: 'testing.testRuns',
            component: () => import('@/views/testing/TestRunsListView.vue'),
          },
          {
            path: 'projects/:projectId/test-runs/:runId',
            name: 'testing.testRuns.detail',
            component: () => import('@/views/testing/TestRunDetailView.vue'),
          },
          {
            path: 'projects/:projectId/scenarios/new',
            name: 'testing.scenarios.create',
            component: () => import('@/views/design/ScenarioEditView.vue'),
          },
          {
            path: 'projects/:projectId/scenarios/:scenarioId',
            name: 'testing.scenarios.edit',
            component: () => import('@/views/design/ScenarioEditView.vue'),
          },

        ],
      },
      // Playground redirect (backward compat)
      {
        path: 'playground/:projectId?',
        redirect: (to) => ({ name: 'testing.playground', params: to.params }),
      },
      // Monitor Section
      {
        path: 'monitor',
        name: 'monitor',
        redirect: { name: 'monitor.conversations' },
        children: [
          {
            path: 'conversations',
            name: 'monitor.conversations',
            component: () => import('@/views/monitor/ConversationsView.vue'),
          },
          {
            path: 'conversations/:conversationId',
            name: 'monitor.conversationDetail',
            component: () => import('@/views/monitor/ConversationDetailView.vue'),
          },
          {
            path: 'users',
            name: 'monitor.users',
            component: () => import('@/views/monitor/UsersView.vue'),
          },
          {
            path: 'users/:userId',
            name: 'monitor.userDetail',
            component: () => import('@/views/monitor/UserDetailView.vue'),
          },
          {
            path: 'issues',
            name: 'monitor.issues',
            component: () => import('@/views/monitor/IssuesView.vue'),
          },
          {
            path: 'issues/:issueId',
            name: 'monitor.issueDetail',
            component: () => import('@/views/monitor/IssueDetailView.vue'),
          },
          {
            path: 'analytics',
            name: 'monitor.analytics',
            component: () => import('@/views/monitor/AnalyticsView.vue'),
            redirect: { name: 'monitor.analytics.latency' },
            children: [
              {
                path: 'latency',
                name: 'monitor.analytics.latency',
                component: () => import('@/views/monitor/LatencyAnalysisView.vue'),
              },
              {
                path: 'token-usage',
                name: 'monitor.analytics.tokenUsage',
                component: () => import('@/views/monitor/TokenUsageAnalysisView.vue'),
              },
              {
                path: 'explore',
                name: 'monitor.analytics.explore',
                component: () => import('@/views/monitor/ExploreView.vue'),
              },
              {
                path: 'funnels',
                name: 'monitor.analytics.funnels',
                component: () => import('@/views/monitor/FunnelView.vue'),
              },
            ],
          },
          {
            path: 'audit-logs',
            name: 'monitor.auditLogs',
            component: () => import('@/views/monitor/AuditLogsView.vue'),
          },
          {
            path: 'audit-logs/:auditLogId',
            name: 'monitor.auditLogDetail',
            component: () => import('@/views/monitor/AuditLogDetailView.vue'),
          },
          {
            path: 'distribution-lists',
            name: 'monitor.distributionLists',
            component: () => import('@/views/monitor/DistributionListsView.vue'),
          },
          {
            path: 'distribution-lists/:listId',
            name: 'monitor.distributionListDetail',
            component: () => import('@/views/monitor/DistributionListDetailView.vue'),
          },
          {
            path: 'outbound-campaigns',
            name: 'monitor.outboundCampaigns',
            component: () => import('@/views/monitor/OutboundCampaignsView.vue'),
          },
          {
            path: 'outbound-campaigns/:campaignId',
            name: 'monitor.outboundCampaignDetail',
            component: () => import('@/views/monitor/OutboundCampaignDetailView.vue'),
          },
        ],
      },
      // Administration Section
      {
        path: 'administration',
        name: 'administration',
        redirect: { name: 'administration.projects' },
        children: [
          {
            path: 'projects',
            name: 'administration.projects',
            component: () => import('@/views/administration/ProjectsView.vue'),
          },
          {
            path: 'projects/new',
            name: 'administration.projects.create',
            component: () => import('@/views/administration/ProjectEditView.vue'),
          },
          {
            path: 'projects/:projectId',
            name: 'administration.projects.edit',
            component: () => import('@/views/administration/ProjectEditView.vue'),
          },
          {
            path: 'api-keys',
            name: 'administration.apiKeys',
            component: () => import('@/views/administration/ApiKeysView.vue'),
          },
          {
            path: 'operators',
            name: 'administration.operators',
            component: () => import('@/views/administration/OperatorsView.vue'),
          },
          {
            path: 'operators/new',
            name: 'administration.operators.create',
            component: () => import('@/views/administration/OperatorEditView.vue'),
          },
          {
            path: 'operators/:operatorId',
            name: 'administration.operators.edit',
            component: () => import('@/views/administration/OperatorEditView.vue'),
          },
          {
            path: 'providers',
            name: 'administration.providers',
            component: () => import('@/views/administration/ProvidersView.vue'),
          },
          {
            path: 'providers/new',
            name: 'administration.providers.create',
            component: () => import('@/views/administration/ProviderEditView.vue'),
          },
          {
            path: 'providers/:providerId',
            name: 'administration.providers.edit',
            component: () => import('@/views/administration/ProviderEditView.vue'),
          },
          {
            path: 'environments',
            name: 'administration.environments',
            component: () => import('@/views/administration/EnvironmentsView.vue'),
          },
          {
            path: 'environments/new',
            name: 'administration.environments.create',
            component: () => import('@/views/administration/EnvironmentEditView.vue'),
          },
          {
            path: 'environments/:environmentId',
            name: 'administration.environments.edit',
            component: () => import('@/views/administration/EnvironmentEditView.vue'),
          },
          {
            path: 'methodologies',
            name: 'administration.methodologies',
            component: () => import('@/views/administration/MethodologiesView.vue'),
          },
          {
            path: 'methodologies/new',
            name: 'administration.methodologies.create',
            component: () => import('@/views/administration/MethodologyEditView.vue'),
          },
          {
            path: 'methodologies/:methodologyId',
            name: 'administration.methodologies.edit',
            component: () => import('@/views/administration/MethodologyEditView.vue'),
          },
          {
            path: 'ai-assistance',
            name: 'administration.aiAssistance',
            component: () => import('@/views/administration/AIAssistanceView.vue'),
          },
          {
            path: 'ai-assistance/skills/new',
            name: 'administration.aiAssistance.skills.create',
            component: () => import('@/views/administration/BuilderSkillEditView.vue'),
          },
          {
            path: 'ai-assistance/skills/:skillId',
            name: 'administration.aiAssistance.skills.edit',
            component: () => import('@/views/administration/BuilderSkillEditView.vue'),
          },
          {
            path: 'ai-assistance/operation-modes/new',
            name: 'administration.aiAssistance.operationModes.create',
            component: () => import('@/views/administration/BuilderOperationModeEditView.vue'),
          },
          {
            path: 'ai-assistance/operation-modes/:operationModeId',
            name: 'administration.aiAssistance.operationModes.edit',
            component: () => import('@/views/administration/BuilderOperationModeEditView.vue'),
          },
          { path: 'benchmark-suites', name: 'administration.benchmarkSuites', component: () => import('@/views/administration/benchmark/BenchmarkSuitesView.vue') },
          { path: 'benchmark-suites/new', name: 'administration.benchmarkSuites.create', component: () => import('@/views/administration/benchmark/BenchmarkSuiteEditView.vue') },
          { path: 'benchmark-suites/:suiteId', name: 'administration.benchmarkSuites.edit', component: () => import('@/views/administration/benchmark/BenchmarkSuiteEditView.vue') },
          { path: 'benchmark-suites/:suiteId/configs/new', name: 'administration.benchmarkSuites.configs.create', component: () => import('@/views/administration/benchmark/BenchmarkConfigEditView.vue') },
          { path: 'benchmark-suites/:suiteId/configs/:configId', name: 'administration.benchmarkSuites.configs.edit', component: () => import('@/views/administration/benchmark/BenchmarkConfigEditView.vue') },
          { path: 'benchmark-runs/:runId', name: 'administration.benchmarkRuns.detail', component: () => import('@/views/administration/benchmark/BenchmarkRunDetailView.vue') },
          { path: 'benchmark-provider-configs', name: 'administration.benchmarkProviderConfigs', component: () => import('@/views/administration/benchmark/BenchmarkProviderConfigsView.vue') },
          { path: 'benchmark-provider-configs/new', name: 'administration.benchmarkProviderConfigs.create', component: () => import('@/views/administration/benchmark/BenchmarkProviderConfigEditView.vue') },
          { path: 'benchmark-provider-configs/:providerConfigId', name: 'administration.benchmarkProviderConfigs.edit', component: () => import('@/views/administration/benchmark/BenchmarkProviderConfigEditView.vue') },
        ],
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)

  // Always check setup status first (except when already on setup page)
  if (to.name !== 'setup' && !authStore.isAuthenticated) {
    try {
      const status = await authStore.checkSetupStatus()
      if (!status.isSetup) {
        // Setup is incomplete, redirect to setup
        next({ name: 'setup' })
        return
      }
    } catch (error) {
      // If setup check fails, log and continue (assume setup is complete)
      console.warn('Failed to check setup status:', error)
    }
  }

  // Handle authentication for protected routes
  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if ((to.name === 'login' || to.name === 'setup') && authStore.isAuthenticated) {
    // Authenticated users navigating to login/setup go to dashboard instead
    next({ name: 'dashboard' })
  } else if (requiresAuth && authStore.isAuthenticated) {
    // Ensure we have profile data when accessing protected routes
    try {
      await authStore.ensureProfile()
      next()
    } catch (error) {
      // If profile fetch fails, redirect to login
      next({ name: 'login', query: { redirect: to.fullPath } })
    }
  } else {
    next()
  }
})

export default router
